import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, Loader2 } from "lucide-react";
import {
  getProviderStatus,
  getUsageSummary,
  type UsageSummary,
} from "@/lib/console-api";
import { useWorkspaceStore } from "@/stores/workspace-store";

function formatDuration(ms: number): string {
  if (ms < 1_000) return `${ms}ms`;
  const sec = ms / 1_000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = sec / 60;
  if (min < 60) return `${min.toFixed(1)}분`;
  return `${(min / 60).toFixed(1)}시간`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatCost(decimalString: string): string {
  const n = Number.parseFloat(decimalString || "0");
  if (Number.isNaN(n)) return "$0";
  if (n === 0) return "$0";
  return `$${n.toFixed(2)}`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "방금 전";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}분 전`;
  return d.toLocaleString();
}

export function UsagePage(): JSX.Element {
  // 워크스페이스 store 가 글로벌 필터 — 사이드바 스위처가 변경.
  const orgId = useWorkspaceStore((s) => s.selectedOrgId);

  const summaryQuery = useQuery({
    queryKey: ["usage-summary", { organizationId: orgId }],
    queryFn: () => getUsageSummary({ organizationId: orgId ?? undefined }),
    enabled: !!orgId,
  });

  const providerQuery = useQuery({
    queryKey: ["provider-status", orgId],
    queryFn: () => getProviderStatus(orgId ?? ""),
    enabled: !!orgId,
  });

  const summary: UsageSummary | undefined = summaryQuery.data;

  return (
    <div className="space-y-6 p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">사용량</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">
            AI 사용량 / 한도
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            누가 얼마나 썼는지, provider 한도에 걸렸는지 한눈에.
          </p>
        </div>
      </header>

      {/* Provider 한도 — 조직 단위 (워크스페이스 선택 필요) */}
      {orgId && (
        <ProviderStatusSection
          loading={providerQuery.isLoading}
          data={providerQuery.data ?? []}
        />
      )}

      {summaryQuery.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {summary && (
        <>
          {/* 요약 카드 */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              label="실행 횟수"
              value={`${formatNumber(summary.totals.count)}회`}
            />
            <SummaryCard
              label="누적 비용"
              value={formatCost(summary.totals.totalEstimatedCost)}
              hint="provider 추정치"
            />
            <SummaryCard
              label="총 입력 토큰"
              value={formatNumber(summary.totals.totalInputTokens)}
            />
            <SummaryCard
              label="총 출력 토큰"
              value={formatNumber(summary.totals.totalOutputTokens)}
            />
          </section>

          {/* 멤버별 / provider 별 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Bucket
              title="멤버별"
              empty="아직 활동이 없어요"
              rows={summary.byAccount.map((a) => ({
                key: a.accountId ?? "unknown",
                left: a.accountId ?? "(미식별)",
                right: `${formatNumber(a.count)}회 · ${formatDuration(a.totalDurationMs)}`,
              }))}
            />
            <Bucket
              title="Provider 별"
              empty="아직 활동이 없어요"
              rows={summary.byProvider.map((p) => ({
                key: p.provider ?? "unknown",
                left: p.provider ?? "(미식별)",
                right: `${formatNumber(p.count)}회 · in ${formatNumber(p.totalInputTokens)} / out ${formatNumber(p.totalOutputTokens)}`,
              }))}
            />
          </div>

          {/* 프로젝트별 */}
          <Bucket
            title="프로젝트별"
            empty="프로젝트 활동이 없어요"
            rows={summary.byProject.map((p) => ({
              key: p.projectId ?? "unknown",
              left: p.projectId ?? "(미식별)",
              right: `${formatNumber(p.count)}회 · ${formatCost(p.totalEstimatedCost)} · ${formatDuration(p.totalDurationMs)}`,
            }))}
          />
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <BarChart3 className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Bucket({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: { key: string; left: string; right: string }[];
  empty: string;
}): JSX.Element {
  return (
    <section className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {rows.map((r) => (
            <li
              key={r.key}
              className="flex items-center justify-between gap-3 py-2.5 text-sm"
            >
              <span className="truncate font-mono text-xs">{r.left}</span>
              <span className="shrink-0 text-muted-foreground">{r.right}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ProviderStatusSection({
  loading,
  data,
}: {
  loading: boolean;
  data: { provider: string; rateLimitedCount: number; lastOccurredAt: string }[];
}): JSX.Element {
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Provider 한도 확인 중…
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm">
        최근 30분 동안 한도(rate-limit) 발생 0건. 모든 provider 가 정상입니다.
      </div>
    );
  }
  return (
    <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-4 text-sm">
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4 text-orange-500" aria-hidden />
        최근 30분 한도 감지
      </div>
      <ul className="mt-3 space-y-2">
        {data.map((s) => (
          <li
            key={s.provider}
            className="flex items-center justify-between text-xs"
          >
            <span className="font-mono">{s.provider}</span>
            <span className="text-muted-foreground">
              {s.rateLimitedCount}회 · 마지막 {formatRelative(s.lastOccurredAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
